import React from 'react';
import SEOHead from '../components/SEOHead';
import { ArrowRight, Brain, Target, Shield, Users, Database, Cloud, Notebook as Robot, BarChart as ChartBar, Star, Smartphone } from 'lucide-react';
import { Link } from 'react-router-dom';

const About = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About Agility",
    "description": "Learn about our mission, vision, and team of AI experts transforming businesses through intelligent solutions.",
    "url": "https://www.agilitytech.ai/about",
    "mainEntity": {
      "@type": "Organization",
      "name": "Agility",
      "foundingDate": "2025",
      "numberOfEmployees": "50-100",
      "industry": "Information Technology",
      "description": "AI consulting and data analytics company with deep expertise in machine learning, automation, and digital transformation."
    }
  };

  return (
    <div className="pt-16">
      <SEOHead
        title="About Agility | AI Consulting Company Built for Speed & Execution"
        description="Agility is an AI consulting company that ships production-ready AI in 3-8 weeks. 50+ senior AI engineers, Ahmedabad HQ, serving clients in the US, UK, India, and Caribbean. Learn how we deliver AI faster than anyone else."
        keywords="about agility AI consulting, AI consulting company India, AI implementation firm, rapid AI deployment company, senior AI engineers India, AI consulting Ahmedabad"
        url="https://www.agilitytech.ai/about"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-r from-blue-900 to-black overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80"
            alt="Data Visualization"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-5xl">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight">
              About Agility The AI Consulting Company That Ships
            </h1>
            <p className="text-2xl text-gray-200 leading-relaxed max-w-3xl">
              We are an AI consulting company built around one principle: production-ready AI in weeks, not months.
              Senior engineers. Lean teams. Measurable outcomes from day one.
            </p>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80"
                alt="Team collaboration"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">Who We Are</h2>
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                At Agility, we're a tight-knit collective of data engineers, cloud architects and AI specialists 
                united by one purpose: transforming raw information into strategic outcomes. With decades of combined 
                experience across finance, healthcare, retail and beyond, we partner with you to architect solutions 
                that grow and adapt as your business evolves.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            <div className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-2xl shadow-lg border border-gray-800/50">
              <h3 className="text-3xl font-bold text-white mb-6">Our Mission</h3>
              <p className="text-xl text-gray-300 leading-relaxed">
                To empower organizations with end-to-end data solutions from secure ingestion and cleansing 
                to advanced, automated analytics so every decision is backed by real insight.
              </p>
            </div>
            <div className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-2xl shadow-lg border border-gray-800/50">
              <h3 className="text-3xl font-bold text-white mb-6">Our Vision</h3>
              <p className="text-xl text-gray-300 leading-relaxed">
                To become the world's most trusted partner for data innovation, enabling every enterprise 
                to unlock the full power of its information.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Client Testimonials */}
      <section className="py-24 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-12 text-center">What Our Clients Say</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="testimonial-card bg-gray-800/50 p-8 rounded-xl border border-gray-700/50">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-300 mb-6 italic leading-relaxed">
                "I had the pleasure of working with Agility on Data Warehousing and Automation projects. They consistently deliver exceptional results and demonstrate deep technical expertise. Their innovative solutions and dedication to quality have greatly benefited our projects. I highly recommend Agility for any data-driven initiatives."
              </p>
              <div className="flex items-center">
                <img
                  src="https://cdn.theorg.com/db3098f3-4457-4e13-ba82-94fc84878dce_thumb.jpg"
                  alt="Manjit Jhooty, CEO Jhoots Group - Client Testimonial"
                  className="w-12 h-12 rounded-full object-cover mr-4 border-2 border-teal-500/50"
                />
                <div>
                  <p className="text-white font-semibold">Manjit Jhooty</p>
                  <p className="text-gray-400 text-sm">CEO, Jhoots Group</p>
                </div>
              </div>
            </div>

            <div className="testimonial-card bg-gray-800/50 p-8 rounded-xl border border-gray-700/50">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-300 mb-6 italic leading-relaxed">
                "Working with Agility was a game-changer. They didn't just meet deadlines they anticipated problems before they arose and solved them efficiently. Their ability to operate autonomously while maintaining exceptional quality made them an invaluable partner for our projects."
              </p>
              <div className="flex items-center">
                <img
                  src="https://lh3.googleusercontent.com/a-/ALV-UjW0CAQs5vi1mYZ6_yFemjiL1AJ0VdZDKkFr-ok6L9_sVghrcgtHMlAGnHeLoYySwPU5bIs8qBe6_pAwdOXQw2x4F0H0eEbJ9jZpvYKMI3JM4aYa6GkkxAZr07SEv23EJl0m9BiT5xC6NABOWGEtDf9DyR8idaM_YFmuyH4-n-iZaXilQ_jEY7b3hmcTL_Tqi36L1XZjCiyHiCluNczhFThsHGEPFVEbXe2iApG8QNkra96FAS9NO2zvQqvyo2xVuc598QL5V1cOn2NT-ZzEL1vL9wBL7yAJUnzubC1t8ZmU13Ak6jmhvTSaHQ9oNCvL875qgl3LT0hqqUEjXqTKBucOzfRriqJpF3qlhR2Kkoe4fkTAaL4hivgIBIZTEVjHg-dDqg_mSNYLaC8_NYGEiuxO5iUlyJwd0SXr0KZdbdMQPzsetqjkyzaYpVNSYHoIV79Pr72fZaQOm0Ou_GDir3dkVcM9ZQoWjZIWdyUnECfJcQyRdeZLlpfZtktU_aXCU6iglpJpbQaujzv74WxoR0FDxbW-jGp33nR_R9Bl36FuKTJtd0ZCEXBdeoQAgPLDsKBtyf2Eli1Qr7_QZQXxJrtGbASEfdmQc82QN8VMdLiVYCUUddIr5XWJ6jfgsLiYxVIZTCL92h9IZv8v4_ZqPsQN4J8WsEd7U8TeHVhy9tXRnG0f86iPoVUj5twcDxqwOzltHQQFSEQg3kp2-hu21qGZHE2QOCDKXgoZJ9yF5M6l8buS11mB1mlLSr01VvzbaPvZr-oIc3hUyXhCvvFgMFoMQAAGmmIpbquuioIunEsatjoJGuYZHOEBduEI8FqxtyAkgzAs7sJTEZ--5VpPDbBZTs0gtaNzos1V3I-1tsVDEXZhz6NWbW0vsWr92dgQFcCBPEi5soHOn6BbPMJpZGYTzhon-xTKkS6J-NudjGJKHrBYokptCH_bm6-cAlTkwAw8QmGK_mo4hTPT6QuGwVAdxxfVTG0avxox4yNcdH2fKMZycqKh0tBRddhOSqvS3MJUvu1rMt70Ik418DKUaPG6xJj1Xok9W-8uIuSf3Pg9Ol0z69TaVQ=s240-p-k-rw-no"
                  alt="Lindsay Williams, CEO FoodDrop - Client Testimonial"
                  className="w-12 h-12 rounded-full object-cover mr-4 border-2 border-teal-500/50"
                />
                <div>
                  <p className="text-white font-semibold">Lindsay Williams</p>
                  <p className="text-gray-400 text-sm">CEO, FoodDrop</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-16 text-center">Our Expertise</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="expertise-card bg-gray-900/50 backdrop-blur-sm p-8 rounded-2xl hover:shadow-lg transition-shadow border border-gray-800/50">
              <Database className="h-12 w-12 text-teal-400 mb-6" />
              <h3 className="text-2xl font-bold text-white mb-4">Data Engineering</h3>
              <p className="text-gray-300">
                We architect and automate ETL pipelines, delivering clean, reliable data wherever you need it, handling petabytes of data for Fortune 500 enterprises with 99.9% data accuracy.
              </p>
            </div>
            <div className="expertise-card bg-gray-900/50 backdrop-blur-sm p-8 rounded-2xl hover:shadow-lg transition-shadow border border-gray-800/50">
              <Cloud className="h-12 w-12 text-teal-400 mb-6" />
              <h3 className="text-2xl font-bold text-white mb-4">Cloud & Infrastructure</h3>
              <p className="text-gray-300">
                Our team designs resilient, cost-efficient environments on AWS, Azure or GCP using infrastructure-as-code best practices, reducing cloud infrastructure costs by an average of 30% for our clients.
              </p>
            </div>
            <div className="expertise-card bg-gray-900/50 backdrop-blur-sm p-8 rounded-2xl hover:shadow-lg transition-shadow border border-gray-800/50">
              <Brain className="h-12 w-12 text-teal-400 mb-6" />
              <h3 className="text-2xl font-bold text-white mb-4">AI & Machine Learning</h3>
              <p className="text-gray-300">
                We build, train and deploy predictive models from demand forecasting to anomaly detection integrated seamlessly into your operations, improving prediction accuracy by up to 25% and reducing false positives by 15%.
              </p>
            </div>
            <div className="expertise-card bg-gray-900/50 backdrop-blur-sm p-8 rounded-2xl hover:shadow-lg transition-shadow border border-gray-800/50">
              <ChartBar className="h-12 w-12 text-teal-400 mb-6" />
              <h3 className="text-2xl font-bold text-white mb-4">Business Intelligence</h3>
              <p className="text-gray-300">
                We craft dynamic dashboards and interactive reports in Power BI, Tableau or custom web apps for real-time visibility, enabling real-time decision-making and accelerating reporting cycles by 80%.
              </p>
            </div>
            <div className="expertise-card bg-gray-900/50 backdrop-blur-sm p-8 rounded-2xl hover:shadow-lg transition-shadow border border-gray-800/50">
              <Robot className="h-12 w-12 text-teal-400 mb-6" />
              <h3 className="text-2xl font-bold text-white mb-4">Automation & Orchestration</h3>
              <p className="text-gray-300">
                Using tools like n8n and Airflow, we eliminate manual bottlenecks and scale workflows with confidence, automating complex business processes that save clients hundreds of thousands annually.
              </p>
            </div>
            <div className="expertise-card bg-gray-900/50 backdrop-blur-sm p-8 rounded-2xl hover:shadow-lg transition-shadow border border-gray-800/50">
              <Target className="h-12 w-12 text-teal-400 mb-6" />
              <h3 className="text-2xl font-bold text-white mb-4">Consulting & Strategy</h3>
              <p className="text-gray-300">
                We deliver architecture reviews, data governance frameworks and roadmaps that align technology investments with your business goals, guiding enterprises through digital transformations that yield a 2x increase in operational efficiency.
              </p>
            </div>
            <div className="expertise-card bg-gray-900/50 backdrop-blur-sm p-8 rounded-2xl hover:shadow-lg transition-shadow border border-gray-800/50">
              <Smartphone className="h-12 w-12 text-teal-400 mb-6" />
              <h3 className="text-2xl font-bold text-white mb-4">Web & Mobile Development</h3>
              <p className="text-gray-300">
                We build custom web and mobile applications using React, React Native, and Flutter, delivering seamless user experiences across all platforms with modern, scalable architecture.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-gradient-to-r from-blue-900 to-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold text-white mb-8">Ready to Transform Your Data?</h2>
            <p className="text-xl text-gray-300 mb-12">
              Let's explore how we can accelerate your next project and empower your organization 
              with the insights it deserves.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-3 bg-teal-500 text-white px-8 py-4 
                rounded-lg text-lg font-medium hover:bg-teal-400 transition-all duration-300 group
                shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              <span>Schedule a Strategic Consultation</span>
              <ArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;